/**
 * Markdown to HTML converter for clipboard operations
 * Designed for compatibility with Word and Excel paste
 */

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Convert a markdown table to HTML table with Word/Excel-compatible styling
 */
export function convertMarkdownTableToHtml(tableMarkdown: string): string {
  const lines = tableMarkdown.split('\n').filter(line => line.trim());
  if (lines.length < 2) return tableMarkdown;

  const rows: string[][] = [];
  let headerRow: string[] | null = null;
  let separatorFound = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines
    if (!line) continue;

    // Check for separator line (|---|---|)
    if (line.match(/^\|[\s\-:|]+\|$/)) {
      separatorFound = true;
      continue;
    }

    // Parse cells - handle lines that start and end with |
    if (line.startsWith('|') && line.endsWith('|')) {
      const cells = line
        .slice(1, -1) // Remove first and last |
        .split('|')
        .map(cell => cell.trim());

      if (cells.length > 0) {
        if (!separatorFound && headerRow === null) {
          headerRow = cells;
        } else {
          rows.push(cells);
        }
      }
    }
  }

  // If no separator found, this isn't a proper table
  if (!separatorFound) return tableMarkdown;

  // Build HTML table with Word/Excel-compatible inline styles
  let html = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0;">';

  // Header row
  if (headerRow) {
    html += '<thead><tr>';
    for (const cell of headerRow) {
      html += `<th style="border: 1px solid #000000; padding: 8px 12px; background-color: #f0f0f0; font-weight: bold; text-align: left;">${escapeHtml(cell)}</th>`;
    }
    html += '</tr></thead>';
  }

  // Data rows
  if (rows.length > 0) {
    html += '<tbody>';
    for (const row of rows) {
      html += '<tr>';
      for (const cell of row) {
        html += `<td style="border: 1px solid #000000; padding: 8px 12px;">${escapeHtml(cell)}</td>`;
      }
      html += '</tr>';
    }
    html += '</tbody>';
  }

  html += '</table>';
  return html;
}

/**
 * Check if content contains a markdown table
 */
export function hasMarkdownTable(content: string): boolean {
  const lines = content.split('\n');
  let hasPipeLines = false;
  let hasSeparator = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      hasPipeLines = true;
      if (trimmed.match(/^\|[\s\-:|]+\|$/)) {
        hasSeparator = true;
      }
    }
  }

  return hasPipeLines && hasSeparator;
}

/**
 * Extract markdown tables from content and return them with their positions
 */
function extractTables(content: string): { start: number; end: number; table: string }[] {
  const lines = content.split('\n');
  const tables: { start: number; end: number; table: string }[] = [];

  let tableStart = -1;
  let tableLines: string[] = [];
  let currentPos = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const lineStart = currentPos;
    currentPos += line.length + 1; // +1 for newline

    const isTableLine = trimmed.startsWith('|') && trimmed.endsWith('|');

    if (isTableLine) {
      if (tableStart === -1) {
        tableStart = lineStart;
      }
      tableLines.push(line);
    } else {
      // End of table (if we were in one)
      if (tableLines.length > 0) {
        const tableContent = tableLines.join('\n');
        if (hasMarkdownTable(tableContent)) {
          tables.push({
            start: tableStart,
            end: lineStart - 1,
            table: tableContent
          });
        }
        tableLines = [];
        tableStart = -1;
      }
    }
  }

  // Handle table at end of content
  if (tableLines.length > 0) {
    const tableContent = tableLines.join('\n');
    if (hasMarkdownTable(tableContent)) {
      tables.push({
        start: tableStart,
        end: currentPos,
        table: tableContent
      });
    }
  }

  return tables;
}

/**
 * Convert full markdown content to HTML with table support
 * Enhanced version for clipboard operations
 */
export async function convertMarkdownToHtml(markdown: string): Promise<string> {
  let html = markdown;

  // Extract tables and replace with placeholders
  const tables = extractTables(markdown);
  const placeholders: { placeholder: string; html: string }[] = [];

  // Process tables in reverse order to maintain position accuracy
  for (let i = tables.length - 1; i >= 0; i--) {
    const { start, end, table } = tables[i];
    const placeholder = `__TABLE_${i}_PLACEHOLDER__`;
    const tableHtml = convertMarkdownTableToHtml(table);
    placeholders.unshift({ placeholder, html: tableHtml });
    html = html.slice(0, start) + placeholder + html.slice(end);
  }

  // Convert other markdown elements
  html = html
    // Headers
    .replace(/^### (.*$)/gim, '<h3 style="margin: 16px 0 8px 0; font-size: 1.1em;">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 style="margin: 20px 0 10px 0; font-size: 1.3em;">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 style="margin: 24px 0 12px 0; font-size: 1.5em;">$1</h1>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code style="background-color: #f4f4f4; padding: 2px 6px; border-radius: 3px; font-family: monospace;">$1</code>')
    // Bullet lists
    .replace(/^[\*\-] (.+)$/gim, '<li style="margin: 4px 0;">$1</li>')
    // Numbered lists
    .replace(/^\d+\. (.+)$/gim, '<li style="margin: 4px 0;">$1</li>')
    // Line breaks
    .replace(/\n\n/g, '</p><p style="margin: 12px 0;">')
    .replace(/\n/g, '<br>');

  // Restore tables
  for (const { placeholder, html: tableHtml } of placeholders) {
    html = html.replace(placeholder, '</p>' + tableHtml + '<p style="margin: 12px 0;">');
  }

  // Wrap in styled container
  html = `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; line-height: 1.6;"><p style="margin: 12px 0;">${html}</p></div>`;

  // Clean up empty paragraphs
  html = html.replace(/<p[^>]*>\s*<\/p>/g, '');

  // Wrap consecutive list items in ul/ol
  html = html.replace(/(<li[^>]*>.*?<\/li>\s*)+/gs, (match) => {
    return '<ul style="margin: 8px 0; padding-left: 24px;">' + match + '</ul>';
  });

  return html;
}
